import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import toast from "react-hot-toast";
import api from "../utils/base_url";
import CategorySidebar from "./CategorySidebar";
import CategoryGrid from "./CategoryGrid";
import StockNotifyModal from "./StockNotifyModal";
import "../styles/index.css";
import "../styles/products.css";


const getThumbnailUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  const url = originalUrl.replace("/products/", "/thumbnails/");
  const lastDot = url.lastIndexOf(".");
  if (lastDot === -1) return url;
  return url.substring(0, lastDot) + ".webp";
};

// ── Cart item identity helpers ──────────────────────────────────────────
// Product IDs and Combo IDs are independent sequences (both start at 1), so
// a plain numeric id is NOT safe to use as the cart key. Every cart item now
// carries a namespaced `id` ("product-12") plus a `refId` (the real DB id)
// and `itemType`, used for navigation / API calls.
const cartKey = (id) => `product-${id}`;

const toCartItem = (product) => ({
  ...product,
  id: cartKey(product.id),
  refId: product.id,
  itemType: "product",
});

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);
  const [favorites, setFavorites] = useState([]);

  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  const [notifyModal, setNotifyModal] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const [notifyForm, setNotifyForm] = useState({
    email: "",
    quantity: "",
  });

  const sectionRefs = useRef({});
  // ── NEW: track whether a programmatic scroll is in progress ──
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimer = useRef(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const state = useSelector((state) => state.handleCart);

  const toggleFavorite = async (productId) => {
    if (!user) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    try {
      const res = await api.post("favorites/toggle/", { product_id: productId, auth_id: user.id });
      const isFav = res.data.favorite;

      setFavorites((prev) =>
        isFav ? [...prev, productId] : prev.filter((id) => id !== productId)
      );

      fetchFavorites();
    } catch (err) {
      console.error(err);
      toast.error("Failed, try again");
    }
  };
  useEffect(() => {
    if (loading || products.length === 0) return;

    const savedY = sessionStorage.getItem("products_scroll_y");

    if (!savedY) return;

    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: parseInt(savedY, 10),
            behavior: "instant",
          });

          // Clean after successful restore
          sessionStorage.removeItem(
            "products_scroll_y"
          );
        });
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, products]);

  const fetchFavorites = useCallback(async () => {
    if (!user) return;

    try {
      const res = await api.get(`favorites/ids/?auth_id=${user.id}`);
      setFavorites(res.data.favorites || []);
    } catch (err) {
      console.error(err);
    }
  }, [user]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get("categorylist/");
      setCategories(res.data.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  const fetchProducts = useCallback(async (categoryId = null) => {
    setLoading(true);
    try {
      const url = categoryId
        ? `productfilter/${categoryId}/`
        : `productlist/`;

      const res = await api.get(url);
      setProducts(res.data.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const scrollToCategory = useCallback((categoryId) => {
    const selectedCategory = categories.find(cat => cat.id === categoryId);
    const sectionEl = sectionRefs.current[selectedCategory?.category_name];

    if (!sectionEl) {
      toast.error("No products available in this category!");
      return;
    }

    // Immediately highlight the clicked category in the sidebar
    setSelectedCat(categoryId);

    // Block the IntersectionObserver from overriding our choice while scrolling
    isProgrammaticScroll.current = true;
    clearTimeout(programmaticScrollTimer.current);

    const NAVBAR_HEIGHT = 100;

    const doScroll = () => {
      const y =
        sectionEl.getBoundingClientRect().top +
        window.pageYOffset -
        NAVBAR_HEIGHT;

      window.scrollTo({ top: y, behavior: "smooth" });
    };

    doScroll();

    // Allow IntersectionObserver to resume after scroll animation finishes.
    // 1200 ms covers even slow smooth-scroll on low-end Android devices.
    programmaticScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 1200);
  }, [categories]);

  useEffect(() => {
    fetchCategories();
    fetchFavorites();

    if (!location.state?.categoryId) {
      fetchProducts();
    }
  }, [fetchCategories, fetchProducts, fetchFavorites, location.state]);

  // ── REPLACED rAF scroll listener with IntersectionObserver ─────────────────
  useEffect(() => {
    if (!categories.length || !products.length) return;

    const NAVBAR_HEIGHT = 100;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return;

        let best = null;
        let bestTop = -Infinity;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const top = entry.boundingClientRect.top;
            if (top > bestTop) {
              bestTop = top;
              best = entry;
            }
          }
        });

        if (!best) return;

        const catName = best.target.getAttribute("data-category");
        const catObj = categories.find(c => c.category_name === catName);
        if (catObj) {
          setSelectedCat(catObj.id);
        }
      },
      {
        rootMargin: `-${NAVBAR_HEIGHT}px 0px -40% 0px`,
        threshold: 0,
      }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [categories, products]);
  // ── end IntersectionObserver ────────────────────────────────────────────────


  useEffect(() => {
    const categoryId = location.state?.categoryId;

    if (!categoryId || !categories.length) return;

    setSelectedCat(categoryId);
    fetchProducts(categoryId);

    const attemptScroll = () => {
      const selectedCategory = categories.find((cat) => cat.id === categoryId);
      if (selectedCategory && sectionRefs.current[selectedCategory.category_name]) {
        const sectionEl = sectionRefs.current[selectedCategory.category_name];
        const yOffset = -100;
        const y = sectionEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };

    const scrollTimeout = setTimeout(attemptScroll, 1200);

    return () => clearTimeout(scrollTimeout);
  }, [location.state, categories, fetchProducts]);

  const Loading = () => (
    <div className="grid-container">
      {[...Array(6)].map((_, idx) => (
        <div
          key={idx}
          className="product-card skeleton-card skeleton-padding"
        >
          <Skeleton height={140} className="skeleton-rounded" />
          <Skeleton
            height={20}
            width={120}
            className="skeleton-spacing-top"
          />
          <Skeleton height={12} width="90%" />
          <Skeleton height={12} width="85%" />
          <Skeleton height={12} width="80%" />
          <Skeleton
            height={20}
            width={90}
            className="skeleton-spacing-top"
          />
          <Skeleton
            height={35}
            width="60%"
            className="skeleton-center"
          />
        </div>
      ))}
    </div>
  );

  const getCartQty = (productId) => {
    const item = state.find((p) => p.id === cartKey(productId));
    return item ? item.qty : 0;
  };

  const addItem = (product) => {
    const available = product.available_stock ?? product.stock_quantity;
    const nextQty = getCartQty(product.id) + 1;

    if (nextQty > available) {
      setStockError(prev => ({ ...prev, [product.id]: true }));
      setDisabledQty(prev => ({ ...prev, [product.id]: true }));

      setTimeout(() => {
        setStockError(prev => ({ ...prev, [product.id]: false }));
        setDisabledQty(prev => ({ ...prev, [product.id]: false }));
      }, 2000);

      return;
    }

    dispatch(addCart(toCartItem(product)));
  };

  const removeItem = (product) => {
    dispatch(delCart(toCartItem(product)));

    setStockError(prev => {
      const newState = { ...prev };
      delete newState[product.id];
      return newState;
    });
  };

  const groupedProducts = useMemo(() => {
    const grouped = products.reduce((acc, product) => {
      const categoryName = product.category_name || "Uncategorized";
      if (!acc[categoryName]) acc[categoryName] = [];
      acc[categoryName].push(product);
      return acc;
    }, {});

    Object.keys(grouped).forEach((catName) => {
      grouped[catName].sort((b, a) => a.id - b.id);
    });
    return grouped;
  }, [products]);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const getOrder = (val) => {
        if (val === null || val === undefined || val === "") return 9999;
        const num = Number(val);
        return isNaN(num) ? 9999 : num;
      };
      const orderA = getOrder(a.order);
      const orderB = getOrder(b.order);
      if (orderA !== orderB) return orderA - orderB;
      return (a.category_name || "").localeCompare(b.category_name || "");
    });
  }, [categories]);

  const RenderProducts = () => {
    return (
      <>
        {sortedCategories
          .filter((cat) => groupedProducts[cat.category_name])
          .map((cat) => {
            const categoryName = cat.category_name;
            const productList = groupedProducts[categoryName];
            return (
              <section
                key={categoryName}
                data-category={categoryName}
                ref={(el) => (sectionRefs.current[categoryName] = el)}
                className="mb-5 product-section"
              >
                <h2>
                  {categoryName}
                </h2>

                <div className="grid-container">
                  {productList.map((product, idx) => {
                    const hasOffer =
                      product.offer_percentage &&
                      Number(product.offer_percentage) > 0 &&
                      product.offer_price;

                    const altCardClass = idx % 2 === 0 ? "card-even" : "card-odd";
                    const available = product.available_stock ?? product.stock_quantity;
                    const outOfStock = available === 0;
                    const stockLow = available <= 2 && available > 0;

                    return (
                      <div
                        key={product.id}
                        className={`product-card ${altCardClass}`}
                        onClick={() => {
                          if (!outOfStock) {
                            sessionStorage.setItem(
                              "products_scroll_y",
                              window.scrollY
                            );

                            navigate(`/product/${product.id}`);
                          }
                        }}
                      >
                        {hasOffer && (
                          <span className="offer-badge">
                            {Math.round(product.offer_percentage)}% OFF
                          </span>
                        )}
                        {outOfStock && (
                          <span className="stock-badge out-stock">Out of Stock</span>
                        )}
                        {!outOfStock && stockLow && (
                          <span className="stock-badge low-stock">
                            Hurry! Only {available} left
                          </span>
                        )}

                        {stockError[product.id] && (
                          <motion.div
                            initial={{ x: 100, opacity: 0 }}
                            animate={{
                              x: [100, 0, -6, 6, -6, 6, 0],
                              opacity: 1,
                            }}
                            transition={{
                              duration: 0.6,
                              ease: "easeOut",
                            }}
                            exit={{
                              x: -100,
                              opacity: 0,
                              transition: { duration: 0.3 }
                            }}
                            className="stock-error-badge"
                          >
                            Stock limit reached!
                          </motion.div>
                        )}

                        <div className="img-wrapper">
                          <img
                            src={getThumbnailUrl(product.product_image)}
                            alt={product.product_name}
                            loading="lazy"
                            decoding="async"
                            className="img-fallback-bg"
                            onError={(e) => { if (e.target.src !== product.product_image) e.target.src = product.product_image; }}
                          />
                        </div>
                        <div className="product-content">
                          <div className="product-title">
                            {product.product_name.length > 15
                              ? product.product_name.substring(0, 15) + "..."
                              : product.product_name}
                          </div>

                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(product.id);
                            }}
                            className="fav-circle-wrap">
                            {user ? (
                              <div className="fav-circle">
                                {favorites.includes(product.id) ? (
                                  <span className="favorite-icon active" >❤️</span>
                                ) : (
                                  <span className="favorite-icon inactive" >🤍</span>
                                )}
                              </div>
                            ) : null}
                          </div>
                          <p className="product-desc">{product.product_description}</p>
                          <div className="product-price-wrap">
                            {hasOffer ? (
                              <>
                                <span className="product-price-offer">
                                  ₹ {parseFloat(product.offer_price).toLocaleString()}
                                </span>
                                <span className="product-price-original">
                                  <s>₹ {parseFloat(product.price).toLocaleString()}</s>
                                </span>
                              </>
                            ) : (
                              <span className="product-price">
                                ₹ {parseFloat(product.price).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="product-btns">
                          <div className="product-btns" onClick={(e) => e.stopPropagation()}>
                            {product.stock_quantity === 0 ? (
                              <div className="notify-wrap">
                                <button
                                  className="notify-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNotifyProduct(product);
                                    setNotifyForm({
                                      email: "",
                                      quantity: "",
                                    });
                                    setNotifyModal(true);
                                  }}
                                >
                                  <span className="notify-icon">🔔</span>
                                  Notify Me
                                </button>
                              </div>
                            ) : getCartQty(product.id) === 0 ? (
                              <button
                                className="btn-buy"
                                onClick={() => addItem(product)}
                              >
                                Add to Cart
                              </button>
                            ) : (
                              <div className="qty-box">
                                <button className="qty-btn" onClick={() => removeItem(product)}>
                                  <i className="fas fa-minus"></i>
                                </button>
                                <span className="qty-value">{getCartQty(product.id)}</span>
                                <button
                                  className="qty-btn"
                                  onClick={() => addItem(product)}
                                  disabled={disabledQty[product.id]}
                                >
                                  <i className="fas fa-plus"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        <StockNotifyModal
          open={notifyModal}
          onClose={() => setNotifyModal(false)}
          product={notifyProduct}
          notifyForm={notifyForm}
          setNotifyForm={setNotifyForm}
          navigate={navigate}
        />
      </>
    );
  };

  return (
    <>
      <div className="container-fluid px-4 mt-4">
        <CategoryGrid
          categories={categories}
          onCategoryClick={scrollToCategory}
        />
      </div>
      <div className="d-flex align-items-start">
        <div className="d-flex align-items-start products-wrapper">
          <div className="sidebar-follow">
            <CategorySidebar
              categories={categories}
              selectedCat={selectedCat}
              handleCategory={(id) => {
                navigate(`/category/${id}`);
              }}
            />
          </div>

          <div className="products-content">
            <h2
              id="product-title"
              className="display-6 fw-bold text-center text-uppercase mb-4"
            >
              Our Products
            </h2>
            {loading ? <Loading /> : RenderProducts()}
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;